// Local copy of the capture's asset grammar (D8-bis): the two repos enforce the SAME closed
// grammar, copied and never imported, so the replay treats a marked <svg> as hostile too.

const NODE_ELEMENT = 2;
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

export const SVG_TAGS: ReadonlySet<string> = new Set(["svg", "g", "path", "circle", "ellipse", "rect", "line", "polyline", "polygon"]);
export const MAX_ASSET_NODES = 400;
export const MAX_ASSET_BYTES = 32 * 1024;
export const MAX_SCENE_ASSET_BYTES = 96 * 1024;

export const CSS_COLOR_NAMES: ReadonlySet<string> = new Set([
    "aliceblue", "antiquewhite", "aqua", "aquamarine", "azure", "beige", "bisque", "black", "blanchedalmond", "blue",
    "blueviolet", "brown", "burlywood", "cadetblue", "chartreuse", "chocolate", "coral", "cornflowerblue", "cornsilk", "crimson",
    "cyan", "darkblue", "darkcyan", "darkgoldenrod", "darkgray", "darkgreen", "darkgrey", "darkkhaki", "darkmagenta", "darkolivegreen",
    "darkorange", "darkorchid", "darkred", "darksalmon", "darkseagreen", "darkslateblue", "darkslategray", "darkslategrey", "darkturquoise", "darkviolet",
    "deeppink", "deepskyblue", "dimgray", "dimgrey", "dodgerblue", "firebrick", "floralwhite", "forestgreen", "fuchsia", "gainsboro",
    "ghostwhite", "gold", "goldenrod", "gray", "green", "greenyellow", "grey", "honeydew", "hotpink", "indianred",
    "indigo", "ivory", "khaki", "lavender", "lavenderblush", "lawngreen", "lemonchiffon", "lightblue", "lightcoral", "lightcyan",
    "lightgoldenrodyellow", "lightgray", "lightgreen", "lightgrey", "lightpink", "lightsalmon", "lightseagreen", "lightskyblue", "lightslategray", "lightslategrey",
    "lightsteelblue", "lightyellow", "lime", "limegreen", "linen", "magenta", "maroon", "mediumaquamarine", "mediumblue", "mediumorchid",
    "mediumpurple", "mediumseagreen", "mediumslateblue", "mediumspringgreen", "mediumturquoise", "mediumvioletred", "midnightblue", "mintcream", "mistyrose", "moccasin",
    "navajowhite", "navy", "oldlace", "olive", "olivedrab", "orange", "orangered", "orchid", "palegoldenrod", "palegreen",
    "paleturquoise", "palevioletred", "papayawhip", "peachpuff", "peru", "pink", "plum", "powderblue", "purple", "rebeccapurple",
    "red", "rosybrown", "royalblue", "saddlebrown", "salmon", "sandybrown", "seagreen", "seashell", "sienna", "silver",
    "skyblue", "slateblue", "slategray", "slategrey", "snow", "springgreen", "steelblue", "tan", "teal", "thistle",
    "tomato", "turquoise", "violet", "wheat", "white", "whitesmoke", "yellow", "yellowgreen",
]);

// Every value is length-capped BEFORE any pattern runs, and every pattern is unambiguous
// (linear), so a hostile value costs O(cap) whatever its shape.
const NUMBER = String.raw`-?(\d+(\.\d+)?|\.\d+)`;
const NUMERIC = new RegExp(`^${NUMBER}(px|%|em)?$`);
const VIEW_BOX = new RegExp(`^${NUMBER}(\\s+${NUMBER}){3}$`);
const COLOR_KEYWORDS = new Set(["currentColor", "none", "transparent", "inherit"]);
const COLOR_FUNCTION = /^(rgba?|hsla?)\(\s*[\d.%]+((\s*[,/]\s*|\s+)[\d.%]+){2,3}\s*\)$/;
const isColor = (value: string): boolean =>
    /^#[0-9a-fA-F]{3,8}$/.test(value) || COLOR_FUNCTION.test(value) || COLOR_KEYWORDS.has(value) || CSS_COLOR_NAMES.has(value.toLowerCase());

const TRANSFORM_FUNCTIONS = new Set(["matrix", "translate", "scale", "rotate", "skewX", "skewY"]);
const isSpace = (char: string): boolean => char === " " || char === "\t" || char === "\n" || char === "\r" || char === "\f";
const isLetter = (char: string): boolean => (char >= "a" && char <= "z") || (char >= "A" && char <= "Z");
const isNumberChar = (char: string): boolean => (char >= "0" && char <= "9") || char === "." || char === "e" || char === "E" || char === "+" || char === "-";

// One left-to-right pass: `name(` number (`,` | spaces) number … `)`, repeated.
const isTransform = (value: string): boolean => {
    let index = 0;
    let functions = 0;
    const skipSpaces = (): boolean => {
        const from = index;
        while (index < value.length && isSpace(value[index])) index += 1;
        return index > from;
    };
    const readWhile = (accept: (char: string) => boolean): string => {
        const from = index;
        while (index < value.length && accept(value[index])) index += 1;
        return value.slice(from, index);
    };
    for (;;) {
        skipSpaces();
        if (index === value.length) return functions > 0;
        if (TRANSFORM_FUNCTIONS.has(readWhile(isLetter)) === false || value[index] !== "(") return false;
        index += 1;
        skipSpaces();
        for (;;) {
            if (readWhile(isNumberChar) === "") return false;
            const spaced = skipSpaces();
            if (value[index] === ")") break;
            if (value[index] === ",") {
                index += 1;
                skipSpaces();
            } else if (spaced === false) return false;
        }
        index += 1;
        functions += 1;
    }
};

type Rule = { max: number; test: (value: string) => boolean };
const rule = (max: number, test: RegExp | ((value: string) => boolean)): Rule =>
    ({ max, test: test instanceof RegExp ? (value) => test.test(value) : test });

const RULES = new Map<string, Rule>([
    ["d", rule(4_000, /^[MmZzLlHhVvCcSsQqTtAa0-9.,\s+\-eE]{1,4000}$/)],
    ["points", rule(4_000, /^[0-9.,\s+\-eE]{1,4000}$/)],
    ["viewBox", rule(512, VIEW_BOX)],
    ...["width", "height", "cx", "cy", "r", "rx", "ry", "x", "y", "x1", "y1", "x2", "y2", "stroke-width", "stroke-miterlimit", "opacity", "fill-opacity", "stroke-opacity"]
        .map((name): [string, Rule] => [name, rule(64, NUMERIC)]),
    ["fill", rule(64, isColor)],
    ["stroke", rule(64, isColor)],
    ["stroke-linecap", rule(64, /^(butt|round|square)$/)],
    ["stroke-linejoin", rule(64, /^(miter|round|bevel|arcs|miter-clip)$/)],
    ["fill-rule", rule(64, /^(nonzero|evenodd)$/)],
    ["clip-rule", rule(64, /^(nonzero|evenodd)$/)],
    ["stroke-dasharray", rule(512, /^[0-9.,\s]{1,64}$/)],
    ["transform", rule(512, isTransform)],
    ["class", rule(Number.POSITIVE_INFINITY, () => true)],
    ["aria-hidden", rule(64, /^(true|false)$/)],
    ["focusable", rule(64, /^(true|false)$/)],
    ["role", rule(64, /^[a-z]{1,20}$/)],
]);

export const SVG_ATTRIBUTES: ReadonlySet<string> = new Set([...Array.from(RULES.keys()), "data-replay-asset"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const hasOwn = (record: Record<string, unknown>, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

const isNodeId = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const tagOf = (node: Record<string, unknown>): string => (typeof node.tagName === "string" ? node.tagName.toLowerCase() : "");

const isSvgElement = (node: unknown): node is Record<string, unknown> =>
    isRecord(node) && node.type === NODE_ELEMENT && isNodeId(node.id) && SVG_TAGS.has(tagOf(node));

const attributes = (source: unknown): Record<string, string> => {
    const out: Record<string, string> = {};
    if (isRecord(source) === false) return out;
    for (const [name, value] of Object.entries(source)) {
        if (name === "data-replay-asset") {
            out[name] = "";
            continue;
        }
        const allowed = RULES.get(name);
        if (allowed !== undefined && typeof value === "string" && value.length <= allowed.max && allowed.test(value)) out[name] = value;
    }
    return out;
};

const isRoot = (node: unknown): boolean =>
    isRecord(node) && node.type === NODE_ELEMENT && tagOf(node) === "svg" && isRecord(node.attributes) && hasOwn(node.attributes, "data-replay-asset");

const child = (node: unknown): Record<string, unknown> | null => {
    if (isSvgElement(node) === false) return null;
    return { type: NODE_ELEMENT, id: node.id, tagName: tagOf(node), attributes: attributes(node.attributes), childNodes: [], isSVG: true };
};

const childrenOf = (node: Record<string, unknown>): unknown[] => (Array.isArray(node.childNodes) ? node.childNodes : []);

const weigh = (node: unknown): { nodes: number; bytes: number } => {
    if (isRecord(node) === false) return { nodes: 0, bytes: 0 };
    const own = isRecord(node.attributes)
        ? Object.values(node.attributes).reduce<number>((sum, value) => sum + (typeof value === "string" ? value.length : 0), 0)
        : 0;
    return childrenOf(node).map(weigh).reduce((total, part) => ({ nodes: total.nodes + part.nodes, bytes: total.bytes + part.bytes }), { nodes: 1, bytes: own });
};

const root = (node: unknown): Record<string, unknown> | null => {
    if (isRecord(node) === false || tagOf(node) !== "svg") return null;
    let count = 0;
    const project = (raw: unknown): Record<string, unknown> | null => {
        const element = child(raw);
        if (element === null || isRecord(raw) === false) return null;
        count += 1;
        if (count > MAX_ASSET_NODES) return null;
        const kept: Record<string, unknown>[] = [];
        for (const entry of childrenOf(raw)) {
            const projected = project(entry);
            if (count > MAX_ASSET_NODES) return null;
            if (projected !== null) kept.push(projected);
        }
        return { ...element, childNodes: kept };
    };
    const projected = project(node);
    if (projected === null || count > MAX_ASSET_NODES) return null;
    const weight = weigh(projected);
    return weight.nodes > MAX_ASSET_NODES || weight.bytes > MAX_ASSET_BYTES ? null : projected;
};

const collectIds = (node: unknown): number[] =>
    isRecord(node) ? [...(isNodeId(node.id) ? [node.id] : []), ...childrenOf(node).flatMap(collectIds)] : [];

const escapeValue = (value: string): string =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const serializeElement = (node: unknown, top: boolean): string => {
    if (isSvgElement(node) === false) return "";
    const tag = tagOf(node);
    if (top && tag !== "svg") return "";
    const attrs = Object.entries(attributes(node.attributes)).map(([name, value]) => ` ${name}="${escapeValue(value)}"`).join("");
    const namespace = top ? ` xmlns="${SVG_NAMESPACE}"` : "";
    return `<${tag}${namespace}${attrs}>${childrenOf(node).map((entry) => serializeElement(entry, false)).join("")}</${tag}>`;
};

const serialize = (node: unknown): string => serializeElement(node, true);

// A parsed SVG document as serialized nodes with local ids. Only SVG-namespace (or, for a
// bare `<svg/>`, namespace-less) elements are converted, so a parser error element or XHTML
// markup never enters; the namespace is re-added by serialize.
const isSvgNamespace = (element: Element): boolean => element.namespaceURI === SVG_NAMESPACE || element.namespaceURI === null;

const fromDocument = (doc: Document): Record<string, unknown> | null => {
    if (doc.getElementsByTagName("parsererror").length > 0) return null;
    const top = doc.documentElement;
    if (top === null || isSvgNamespace(top) === false || top.localName !== "svg") return null;
    let next = 0;
    const convert = (element: Element): Record<string, unknown> | null => {
        next += 1;
        if (next > MAX_ASSET_NODES) return null;
        const id = next;
        const kept: Record<string, unknown>[] = [];
        for (const entry of Array.from(element.children)) {
            if (isSvgNamespace(entry) === false) continue;
            const converted = convert(entry);
            if (converted === null) return null;
            kept.push(converted);
        }
        return {
            type: NODE_ELEMENT,
            id,
            tagName: element.localName,
            attributes: Object.fromEntries(Array.from(element.attributes, (attribute) => [attribute.name, attribute.value])),
            childNodes: kept,
            isSVG: true,
        };
    };
    return convert(top);
};

export const svgProjection = { root, child, attributes, isRoot, serialize, fromDocument, collectIds, weigh };
